import { z } from 'zod';
import { AUDIT_ACTIONS } from '../enums.js';
/**
 * The audit trail screen — who did what, when. Read only.
 *
 * Not to be confused with the "Activity log" (/activity), which lists the
 * calls, visits and emails salespeople LOG about clients. This is the
 * system's own record of every create, edit, delete, sign-in and import,
 * written by recordAudit() on the server.
 */
/** Smaller than MAX_PAGE_SIZE on purpose: nothing needs all 6,500 rows at once. */
export const AUDIT_LOG_MAX_PAGE_SIZE = 100;
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`))
    && new Date(`${v}T00:00:00Z`).toISOString().slice(0, 10) === v, 'Not a real date');
/** Blank query values ("?actorId=") mean "no filter", not a validation error. */
const blankToUndefined = (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v);
export const auditLogQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(AUDIT_LOG_MAX_PAGE_SIZE).default(50),
    actorId: z.preprocess(blankToUndefined, z.string().uuid().optional()),
    entityType: z.preprocess(blankToUndefined, z.string().trim().max(64).optional()),
    action: z.preprocess(blankToUndefined, z.enum(AUDIT_ACTIONS).optional()),
    /** Inclusive calendar days, read in Beirut time (see auditDayBounds). */
    from: z.preprocess(blankToUndefined, isoDate.optional()),
    to: z.preprocess(blankToUndefined, isoDate.optional()),
    /** Searches the summary text. */
    q: z.preprocess(blankToUndefined, z.string().trim().max(200).optional()),
}).refine((v) => !v.from || !v.to || v.from <= v.to, {
    message: 'The start date must be on or before the end date',
    path: ['to'],
});
// --------------------------------------------------------- date boundaries
/** The office's clock. Both offices' staff read dates in Beirut terms. */
export const AUDIT_TIME_ZONE = 'Asia/Beirut';
const partsFormatter = new Map();
function formatterFor(timeZone) {
    let f = partsFormatter.get(timeZone);
    if (!f) {
        f = new Intl.DateTimeFormat('en-US', {
            timeZone, hourCycle: 'h23',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
        partsFormatter.set(timeZone, f);
    }
    return f;
}
/** Wall-clock time in `timeZone` at instant `ms`, expressed as if it were UTC. */
function wallClockMs(ms, timeZone) {
    const parts = {};
    for (const p of formatterFor(timeZone).formatToParts(new Date(ms))) {
        if (p.type !== 'literal')
            parts[p.type] = Number(p.value);
    }
    return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
}
/**
 * The first instant of a calendar day in `timeZone`.
 *
 * Not simply "midnight minus the offset": Lebanon changes its clocks AT
 * midnight, so on the spring-forward Sunday 00:00 never happens and the day
 * starts at 01:00. Both offsets in force around the day are tried, and the
 * earliest instant whose local date is that day wins.
 */
export function startOfDayIn(isoDate, timeZone = AUDIT_TIME_ZONE) {
    const midnightUtc = Date.parse(`${isoDate}T00:00:00Z`);
    const day = 24 * 60 * 60 * 1000;
    const offsets = new Set([
        wallClockMs(midnightUtc - day, timeZone) - (midnightUtc - day),
        wallClockMs(midnightUtc + day, timeZone) - (midnightUtc + day),
    ]);
    let best = null;
    for (const offset of offsets) {
        const t = midnightUtc - offset;
        const localDate = new Date(wallClockMs(t, timeZone)).toISOString().slice(0, 10);
        if (localDate === isoDate && (best === null || t < best))
            best = t;
    }
    // Unreachable for a real zone; fall back to the first candidate rather than throw.
    return new Date(best ?? midnightUtc - [...offsets][0]);
}
/** The day after an ISO date, as an ISO date. */
function nextDay(isoDate) {
    const d = new Date(`${isoDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
}
/**
 * The inclusive from/to days as a half-open instant range: createdAt >= gte
 * and createdAt < lt. "To 31 July" therefore includes 23:59:59 on the 31st.
 * Index-friendly: the column is compared with constants, never converted.
 */
export function auditDayBounds(from, to, timeZone = AUDIT_TIME_ZONE) {
    return {
        ...(from ? { gte: startOfDayIn(from, timeZone) } : {}),
        ...(to ? { lt: startOfDayIn(nextDay(to), timeZone) } : {}),
    };
}
//# sourceMappingURL=auditLog.js.map